import { IFormOperationAnswerField, ITextLocaleMap } from '../models/operations.models';
import { FormOperationDetailSectionId } from './form-operation-detail-sections.util';
import { resolveWorkflowLocaleText } from '../../workflows/utils/workflow-locale-text.util';
import {
	findFormFieldPreset,
	inferFormFieldPresetId,
} from '../../workflows/components/modal-workflow-form-step/form-field-presets';
import { NOT_AVAILABLE_DISPLAY } from '../../core/utils/utils';

/** Etiquetas canónicas de campos preset FORM (Figma `9260-133055` / `9260-133626`). */
const FIELD_LABEL_I18N_BY_ID: Record<string, string> = {
	surname: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_SURNAME_FIELD',
	first_surname: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_SURNAME_FIELD',
	surnames: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_SURNAME_FIELD',
	surname2: 'PROCESSES.DETAIL.SURNAMES_2',
	second_surname: 'PROCESSES.DETAIL.SURNAMES_2',
	name: 'PROCESSES.DETAIL.NAME',
	names: 'PROCESSES.DETAIL.NAME',
	gender: 'PROCESSES.DETAIL.GENDER',
	nationality: 'PROCESSES.DETAIL.NACIONALITY',
	birth_date: 'PROCESSES.DETAIL.BIRTH_DATE',
	birthdate: 'PROCESSES.DETAIL.BIRTH_DATE',
	id_document: 'PROCESSES.DETAIL.NUM_DOCUMENT',
	document_number: 'PROCESSES.DETAIL.NUM_DOCUMENT',
	identifier: 'PROCESSES.DETAIL.NUM_DOCUMENT',
	personal_id: 'PROCESSES.DETAIL.PERSONAL_ID',
	expiration_date: 'PROCESSES.DETAIL.EXPIRY_DATE',
	expiry_date: 'PROCESSES.DETAIL.EXPIRY_DATE',
	expiry: 'PROCESSES.DETAIL.EXPIRY_DATE',
	address: 'PROCESSES.DETAIL.ADDRESS',
	city: 'PROCESSES.DETAIL.CITY',
	municipality: 'PROCESSES.DETAIL.CITY',
	province: 'PROCESSES.DETAIL.PROVINCE',
	address_number: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_ADDRESS_NUMBER',
	address_floor: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_ADDRESS_FLOOR',
	address_door: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_ADDRESS_DOOR',
	zip_code: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_ADDRESS_ZIP_CODE',
	country: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_ADDRESS_COUNTRY',
	email: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_EMAIL_FIELD',
	phone: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_PHONE_FIELD',
	phone_prefix: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_PHONE_PREFIX',
};

/** Tipo legible del preset genérico FORM (paréntesis en detalle «Otros datos»). */
const PRESET_TYPE_LABEL_I18N: Record<string, string> = {
	short_text: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELD_TYPE_LABEL_SHORT_TEXT',
	long_text: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELD_TYPE_LABEL_LONG_TEXT',
	multi_select: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_MULTI_SELECT',
	single_select: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELD_TYPE_LABEL_SINGLE_SELECT',
	checkbox: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_CHECKBOX',
	yes_no: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELD_TYPE_LABEL_SINGLE_SELECT',
	legal: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_LEGAL',
	date: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELD_TYPE_LABEL_DATE',
	number: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELD_TYPE_LABEL_NUMBER',
};

/** Presets cuyo título en detalle sigue el i18n del panel, no el snapshot del journey. */
const PANEL_I18N_TITLE_PRESET_IDS = new Set(['yes_no', 'legal', 'checkbox']);

const GENERIC_FIELD_TYPE_LABEL_I18N: Record<string, string> = {
	text: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELD_TYPE_LABEL_SHORT_TEXT',
	textarea: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELD_TYPE_LABEL_LONG_TEXT',
	select: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELD_TYPE_LABEL_SINGLE_SELECT',
	checkbox: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_CHECKBOX',
	radio: 'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELD_TYPE_LABEL_SINGLE_SELECT',
};

export interface IFormOperationFieldLabelField {
	id?: string;
	name?: string;
	title?: string | ITextLocaleMap | null;
	type?: string;
	stepTitle?: string | ITextLocaleMap | null;
}

export interface IFormOperationFieldLabelDeps {
	locale: string;
	translate: (key: string) => string;
	hasTranslation: (key: string) => boolean;
}

function resolveFieldKey(field: Pick<IFormOperationAnswerField, 'id' | 'name'> | IFormOperationFieldLabelField): string {
	return (field.id || field.name || '').trim().toLowerCase();
}

function resolveCanonicalLabelKey(
	fieldKey: string,
	sectionId: FormOperationDetailSectionId,
): string | null {
	if (fieldKey === 'id_document_type' || fieldKey === 'document_type') {
		return sectionId === 'identity_document'
			? 'PROCESSES.DETAIL.FORM_MATCH_DATA_MODAL.TYPE'
			: 'PROCESSES.DETAIL.TYPE_DOCUMENT';
	}

	return FIELD_LABEL_I18N_BY_ID[fieldKey] ?? null;
}

function isCanonicalPresetField(fieldKey: string): boolean {
	return fieldKey in FIELD_LABEL_I18N_BY_ID;
}

function resolveLocalizedText(
	value: string | ITextLocaleMap | null | undefined,
	locale: string,
): string {
	return resolveWorkflowLocaleText(value as string | ITextLocaleMap, locale).trim();
}

function resolvePresetTypeLabel(
	field: IFormOperationFieldLabelField,
	deps: IFormOperationFieldLabelDeps,
): string {
	const presetId = inferFormFieldPresetId(field as Record<string, unknown>);
	const preset = findFormFieldPreset(presetId);
	const key =
		preset?.previewTypeLabelKey ??
		PRESET_TYPE_LABEL_I18N[presetId] ??
		GENERIC_FIELD_TYPE_LABEL_I18N[(field.type ?? '').toLowerCase()];

	if (key && deps.hasTranslation(key)) {
		return deps.translate(key);
	}

	return '';
}

function resolveCustomFieldTitle(
	field: IFormOperationFieldLabelField,
	deps: IFormOperationFieldLabelDeps,
): string {
	const presetId = inferFormFieldPresetId(field as Record<string, unknown>);
	const preset = findFormFieldPreset(presetId);

	if (
		PANEL_I18N_TITLE_PRESET_IDS.has(presetId) &&
		preset?.fieldTitleKey &&
		deps.hasTranslation(preset.fieldTitleKey)
	) {
		return deps.translate(preset.fieldTitleKey);
	}

	const fromField = resolveLocalizedText(field.title, deps.locale);
	if (fromField) {
		return fromField;
	}

	const fromStep = resolveLocalizedText(field.stepTitle, deps.locale);
	if (fromStep) {
		return fromStep;
	}

	if (preset?.fieldTitleKey && deps.hasTranslation(preset.fieldTitleKey)) {
		return deps.translate(preset.fieldTitleKey);
	}

	return (field.name || field.id || NOT_AVAILABLE_DISPLAY).trim();
}

function formatCustomFieldLabel(title: string, typeLabel: string): string {
	if (!typeLabel || typeLabel.toLowerCase() === title.toLowerCase()) {
		return title;
	}

	return `${title} (${typeLabel})`;
}

/**
 * Etiqueta visible del campo en detalle FORM.
 * Presets estándar: copy Figma vía i18n. Campos custom: «título (tipo)» en idioma activo.
 */
export function resolveFormOperationFieldLabel(
	field: IFormOperationFieldLabelField,
	sectionId: FormOperationDetailSectionId,
	deps: IFormOperationFieldLabelDeps,
): string {
	const fieldKey = resolveFieldKey(field);
	const canonicalKey = fieldKey ? resolveCanonicalLabelKey(fieldKey, sectionId) : null;

	if (canonicalKey && deps.hasTranslation(canonicalKey)) {
		return deps.translate(canonicalKey);
	}

	if (!isCanonicalPresetField(fieldKey)) {
		const title = resolveCustomFieldTitle(field, deps);
		const typeLabel = resolvePresetTypeLabel(field, deps);
		return formatCustomFieldLabel(title, typeLabel);
	}

	const fromTitle = resolveLocalizedText(field.title, deps.locale);
	if (fromTitle) {
		return fromTitle;
	}

	return field.name || NOT_AVAILABLE_DISPLAY;
}
